// Tracks observed roots and classifies selector-list parts for render leaves.
// Root inheritance is compiler-owned so runtime contracts stay unchanged.
import type { LuisCompileWarning } from '../types'
import type { ObservedSelectorMode } from './observed-selector'
import { resolveObservedSelectorParts, type ObservedSelectorPartResolution } from './observed-selector'
import { normalizeSelectorForRootMatch, selectorStartsWithRoot } from './selector-list'

export type ObservedRootRegistry = Map<string, ObservedSelectorMode>

export type ClassifiedObservedSelectorPart = {
  selector: string
  observedMode: ObservedSelectorMode
}

// Register explicit observed selector parts as inheritance roots.
export function registerObservedRootsFromSelector(
  selector: string,
  roots: ObservedRootRegistry,
  line: number,
  warnings?: LuisCompileWarning[],
): void {
  const parts = resolveObservedSelectorParts(selector)
  for (const part of parts) {
    if (part.observedMode !== 'none') {
      registerObservedRoot(part, roots, line, warnings)
    }
  }
}

// Classify selector-list parts with explicit markers or inherited roots.
export function classifyObservedSelectorParts(
  selector: string,
  roots: ObservedRootRegistry,
  observedDisabled: boolean,
): ClassifiedObservedSelectorPart[] {
  const parts = resolveObservedSelectorParts(selector)
  return parts.map((part) => {
    if (observedDisabled) {
      return { selector: part.selector, observedMode: 'none' }
    }

    if (part.observedMode !== 'none') {
      return { selector: part.selector, observedMode: part.observedMode }
    }

    if (part.hasMarker) {
      return { selector: part.selector, observedMode: 'none' }
    }

    return {
      selector: part.selector,
      observedMode: findInheritedObservedMode(part.selector, roots),
    }
  })
}

// Add or update one root and warn when its mode changes.
function registerObservedRoot(
  part: ObservedSelectorPartResolution,
  roots: ObservedRootRegistry,
  line: number,
  warnings?: LuisCompileWarning[],
): void {
  const root = normalizeSelectorForRootMatch(part.selector)
  const existingMode = roots.get(root)
  if (existingMode && existingMode !== part.observedMode && warnings) {
    warnings.push({
      code: 'invalid-observed-syntax',
      line,
      message: `Observed root "${part.selector}" was declared with both "${existingMode}" and "${part.observedMode}" modes. The latest explicit marker is used for inherited selectors.`,
    })
  }

  roots.set(root, part.observedMode)
}

// Find the most specific matching root for one selector part.
function findInheritedObservedMode(selector: string, roots: ObservedRootRegistry): ObservedSelectorMode {
  let matchedMode: ObservedSelectorMode = 'none'
  let matchedLength = -1

  for (const [root, mode] of roots) {
    if (mode === 'none') continue
    if (!selectorStartsWithRoot(selector, root)) continue

    if (root.length > matchedLength) {
      matchedMode = mode
      matchedLength = root.length
    }
  }

  return matchedMode
}
